import { addDoc, doc, getDocs, onSnapshot, query, updateDoc } from 'firebase/firestore';
import { firestore } from './firebase';
import { getCurrentDateTime } from 'utils/DateTime';

/**
 * Clase para manejar las operaciones de Firestore.
 */
class FirebaseFirestoreService {
  /**
   * Obtiene el primer documento de una colección que cumpla con las condiciones del constrain.
   * @param {string} collection - La colección.
   * @param {string[]} constrains - Un arreglo de wheres y otras condiciones.
   * @returns {Promise<DocumentSnapshot>} - Un objeto `DocumentSnapshot` que representa el documento.
   */
  static async findDoc(collection, constrains = []) {
    try {
      const q = query(collection, ...constrains);

      const result = await getDocs(q);
      if (result.docs.length === 0) {
        return null;
      }

      const docData = result.docs[0].data();
      const docId = result.docs[0].id;

      return { id: docId, ...docData };
    } catch (error) {
      console.error('Error al obtener el documento de la colección:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los documentos de una colección.
   * @param {string} collection - Colección.
   * @param {string[]} constrains - Un arreglo de wheres y otras condiciones.
   * @returns {Promise<DocumentSnapshot[]>} - Un arreglo de objetos `DocumentSnapshot` que representan todos los documentos de la colección.
   */
  static async getAllDocs(collection, constrains = []) {
    try {
      const q = query(collection, ...constrains);

      const result = await getDocs(q);
      const data = result.docs.map((doc) => Object.assign({ id: doc.id }, doc.data()));

      return data;
    } catch (error) {
      console.error('Error al obtener los documentos de la colección:', error);
      return { success: false, error };
    }
  }

  /**
   * Devuelve el tamaño de una colección.
   * @param {string} collection - Colección.
   * @param {string[]} constrains - Un arreglo de wheres y otras condiciones.
   * @returns {Promise<Number>} - Un numero `Number` que representa la cantidad de documentos de la colección.
   */
  static async getRegisterCount(collection, constrains = []) {
    try {
      const q = query(collection, ...constrains);
      const result = await getDocs(q);
      return { success: true, size: result.size };
    } catch (error) {
      console.error('Error al obtener los documentos de la colección:', error);
      return { success: false, error };
    }
  }

  static subscribeToAllDocs(collection, constrains = [], callback) {
    const q = query(collection, ...constrains);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs.map((doc) => Object.assign({ id: doc.id }, doc.data()));
      callback(data);
    });

    return unsubscribe;
  }

  /**
   * Obtiene un documento de una colección.
   * @param {string} collectionPath - La ruta de la colección.
   * @param {string} documentId - El ID del documento.
   * @returns {Promise<DocumentSnapshot>} - Un objeto `DocumentSnapshot` que representa el documento.
   */
  static async getDoc(collectionPath, documentId) {
    try {
      const docRef = doc(firestore, collectionPath, documentId);
      const doc = await docRef.get();
      return doc;
    } catch (error) {
      console.error('Error al obtener el documento de la colección:', error);
      throw error;
    }
  }

  /**
   * Crea un nuevo documento en una colección.
   * @param {object} collection - Colección.
   * @param {object} data - Los datos del documento.
   * @returns {Promise<DocumentReference>} - Una referencia al documento creado.
   */
  static async addDoc(collection, data) {
    try {
      data.created = getCurrentDateTime();
      data.updated = getCurrentDateTime();
      data.isActive = true;

      const docRef = await addDoc(collection, data);
      return { success: true, docRef };
    } catch (error) {
      console.error('Error al agregar el documento a la colección:', error);
      return { success: false, error };
    }
  }

  /**
   * Actualiza un documento en una colección.
   * @param {string} collectionPath - La ruta de la colección.
   * @param {string} documentId - El ID del documento.
   * @param {object} data - Los datos actualizados del documento.
   * @returns {Promise<void>}
   */
  static async updateDoc(collectionPath, documentId, data) {
    try {
      data.updated = getCurrentDateTime();

      const document = doc(firestore, collectionPath, documentId);
      const result = await updateDoc(document, data);
      return { success: true, result };
    } catch (error) {
      console.error('Error al actualizar el documento en la colección:', error);
      return { success: false, error };
    }
  }

  /**
   * Elimina un documento de una colección.
   * @param {string} collectionPath - La ruta de la colección.
   * @param {string} documentId - El ID del documento.
   * @returns {Promise<void>}
   */
  static async deleteDoc(collectionPath, documentId) {
    try {
      const document = doc(firestore, collectionPath, documentId);
      const result = await updateDoc(document, { isActive: false, updated: getCurrentDateTime() });
      return { success: true, result };
    } catch (error) {
      console.error('Error al actualizar el documento en la colección:', error);
      return { success: false, error };
    }
  }

  static async reactivateDoc(collectionPath, documentId) {
    try {
      const document = doc(firestore, collectionPath, documentId);
      const result = await updateDoc(document, { isActive: true, updated: getCurrentDateTime() });
      return { success: true, result };
    } catch (error) {
      console.error('Error al actualizar el documento en la colección:', error);
      return { success: false, error };
    }
  }

  /**
   * Verifica si existe al menos un documento que cumpla con los criterios dados en una colección.
   * @param {string} collection - La colección.
   * @param {Array} constrains - Un arreglo de condiciones de consulta.
   * @returns {Promise<boolean>} - Devuelve true si existe al menos un documento que cumple con los criterios, de lo contrario devuelve false.
   */
  static async documentExists(collection, constrains = []) {
    try {
      const q = query(collection, ...constrains);
      const querySnapshot = await getDocs(q);

      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error al verificar la existencia de documentos en la colección:', error);
      throw error;
    }
  }
}

export default FirebaseFirestoreService;
